import { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { permissionsService } from '../services/permissionsService';
import { invitationsService } from '../services/invitationsService';
import {
  ROLES,
  ROLE_LABELS,
  USER_STATUS_LABELS,
  type CompanyUserWithProfile,
  type Invitation,
  type Role,
} from '../types/permissions';
import { PageContainer, PageHeading, Card, Button } from '../components/ui';
import RolePermissionsHelp from '../components/users/RolePermissionsHelp';
import { usuarioLabel } from '../helpers/userDisplayHelper';

/**
 * Gestión de usuarios de la empresa activa.
 * Solo accesible para owner/admin (protegido por ruta).
 */
const UsersManagementPage = () => {
  const { user, activeEmpresaId, userRole, refreshPermissions } = useAuth();
  const isOwner = userRole === 'owner';

  const [members, setMembers] = useState<CompanyUserWithProfile[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form de invitación
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('operator');
  const [inviting, setInviting] = useState(false);

  const loadData = useCallback(async () => {
    if (!activeEmpresaId) return;
    setLoading(true);
    setError(null);
    try {
      const [m, i] = await Promise.all([
        permissionsService.getCompanyUsers(activeEmpresaId),
        invitationsService.getPendingInvitations(activeEmpresaId),
      ]);
      setMembers(m);
      setInvitations(i);
    } catch (err: any) {
      setError(err?.message ?? 'Error al cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  }, [activeEmpresaId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInvite = async () => {
    if (!activeEmpresaId || !inviteEmail || !user) return;
    setInviting(true);
    setError(null);
    try {
      await invitationsService.inviteUser(
        { empresaId: activeEmpresaId, email: inviteEmail, role: inviteRole },
        user.id,
      );
      setInviteEmail('');
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo enviar la invitación.');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: Role) => {
    if (!activeEmpresaId) return;
    try {
      await permissionsService.updateUserRole(activeEmpresaId, userId, newRole);
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo cambiar el rol.');
    }
  };

  const handleStatusChange = async (
    userId: string,
    status: 'active' | 'inactive' | 'suspended',
  ) => {
    if (!activeEmpresaId) return;
    try {
      await permissionsService.updateUserStatus(activeEmpresaId, userId, status);
      await loadData();
      if (status !== 'active') await refreshPermissions();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo cambiar el estado.');
    }
  };

  const handleRemove = async (userId: string) => {
    if (!activeEmpresaId) return;
    if (!window.confirm('¿Eliminar a este usuario de la empresa?')) return;
    try {
      await permissionsService.removeUser(activeEmpresaId, userId);
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo eliminar el usuario.');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await invitationsService.cancelInvitation(invitationId);
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo cancelar la invitación.');
    }
  };

  return (
    <Layout>
      <PageContainer size="lg">
        <PageHeading title="Gestión de usuarios" subtitle="Miembros e invitaciones de la empresa" />

        {error && (
          <div className="mb-4 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
            {error}
          </div>
        )}

        {/* Invitar nuevo usuario */}
        <Card pad="md" className="mb-6" data-ar-guide="usuarios-invitar">
          <h2 className="mb-3 font-display text-lg font-semibold text-ink">Invitar usuario</h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              data-ar-guide="usuarios-invitar-email"
              placeholder="email@ejemplo.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 rounded-md border border-hairline bg-white px-3 py-2 text-sm text-ink placeholder:text-sage/70 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
            />
            <select
              data-ar-guide="usuarios-invitar-rol"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              className="rounded-md border border-hairline bg-white px-3 py-2 text-sm text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20"
            >
              {ROLES.filter((r) => r !== 'owner' || isOwner).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail} data-ar-guide="usuarios-btn-invitar">
              {inviting ? 'Enviando…' : 'Invitar'}
            </Button>
          </div>
        </Card>

        {/* Ayuda: qué permisos tiene el rol seleccionado al invitar */}
        <div data-ar-guide="usuarios-role-help">
          <RolePermissionsHelp selectedRole={inviteRole} />
        </div>

        {/* Miembros */}
        <div className="mb-6 overflow-hidden rounded-lg border border-hairline bg-white shadow-card" data-ar-guide="usuarios-miembros">
          <div className="border-b border-hairline px-5 py-3">
            <h2 className="font-display text-lg font-semibold text-ink">Miembros ({members.length})</h2>
          </div>
          {loading ? (
            <p className="p-5 text-sage">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-paper-2 text-left text-sage">
                  <tr>
                    <th className="px-5 py-2 font-medium">Usuario</th>
                    <th className="px-5 py-2 font-medium">Rol</th>
                    <th className="px-5 py-2 font-medium">Estado</th>
                    <th className="px-5 py-2 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {members.map((m) => (
                    <tr key={m.id} className="hover:bg-paper-2/60">
                      <td className="px-5 py-3 text-ink">
                        {usuarioLabel(m.nombre, m.apellido, m.email) ?? m.user_id}
                        {m.email && (m.nombre || m.apellido) && (
                          <span className="block text-xs text-sage">{m.email}</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={m.role}
                          disabled={m.role === 'owner' || !isOwner}
                          onChange={(e) => handleRoleChange(m.user_id, e.target.value as Role)}
                          className="rounded border border-hairline bg-white px-2 py-1 text-ink disabled:bg-paper-2 disabled:text-sage"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`rounded px-2 py-1 text-xs font-medium ${
                            m.status === 'active'
                              ? 'bg-success/10 text-success'
                              : 'bg-paper-2 text-sage'
                          }`}
                        >
                          {USER_STATUS_LABELS[m.status]}
                        </span>
                      </td>
                      <td className="space-x-2 px-5 py-3 text-right">
                        {m.role !== 'owner' && (
                          <>
                            {m.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(m.user_id, 'suspended')}
                                className="font-medium text-warning hover:underline"
                              >
                                Suspender
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(m.user_id, 'active')}
                                className="font-medium text-forest hover:underline"
                              >
                                Activar
                              </button>
                            )}
                            <button
                              onClick={() => handleRemove(m.user_id)}
                              className="font-medium text-danger hover:underline"
                            >
                              Eliminar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Invitaciones pendientes */}
        {invitations.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-hairline bg-white shadow-card" data-ar-guide="usuarios-invitaciones-pendientes">
            <div className="border-b border-hairline px-5 py-3">
              <h2 className="font-display text-lg font-semibold text-ink">
                Invitaciones pendientes ({invitations.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-paper-2 text-left text-sage">
                  <tr>
                    <th className="px-5 py-2 font-medium">Email</th>
                    <th className="px-5 py-2 font-medium">Rol</th>
                    <th className="px-5 py-2 font-medium">Expira</th>
                    <th className="px-5 py-2 text-right font-medium">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {invitations.map((inv) => (
                    <tr key={inv.id} className="hover:bg-paper-2/60">
                      <td className="px-5 py-3 text-ink">{inv.email}</td>
                      <td className="px-5 py-3 text-ink">{ROLE_LABELS[inv.role]}</td>
                      <td className="px-5 py-3 text-sage">{new Date(inv.expires_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => handleCancelInvitation(inv.id)}
                          className="font-medium text-danger hover:underline"
                        >
                          Cancelar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PageContainer>
    </Layout>
  );
};

export default UsersManagementPage;
